import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE", "YT", "NT", "NU"];

export default function Checkout() {
  const { cart, money, country, currency, clearCart, user } = useApp();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [ship, setShip] = useState("standard");
  const [code, setCode] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({
    email: user?.email || "", first: "", last: "", address: "", city: "",
    province: country?.code === "CA" ? "ON" : "", postal: "", phone: "", subscribe: true,
  });

  const items = cart.map((i) => ({ product_id: i.id, qty: i.qty, size: i.size, color: i.color, options: i.options || [] }));

  const refresh = useCallback(async (discount) => {
    if (!cart.length) return;
    try {
      const { data } = await api.post("/checkout/quote", {
        items, country: country?.code || "CA", province: form.province,
        currency, shipping_method: ship, discount_code: discount ?? appliedCode,
      });
      setQuote(data);
    } catch (e) { /* ignore */ }
  }, [cart, country, currency, ship, form.province, appliedCode]);

  useEffect(() => { refresh(); }, [ship, form.province, currency]);
  useEffect(() => { if (user?.email) setForm((f) => ({ ...f, email: user.email })); }, [user]);

  if (!cart.length) return (
    <div className="text-center py-40">
      <p className="font-serif text-3xl mb-4">Your bag is empty</p>
      <button onClick={() => navigate("/")} className="btn-luxe">Continue Shopping</button>
    </div>
  );

  const applyCode = async () => {
    setAppliedCode(code);
    try {
      const { data } = await api.post("/checkout/quote", {
        items, country: country?.code || "CA", province: form.province, currency, shipping_method: ship, discount_code: code,
      });
      setQuote(data);
      if (data.discount?.error) toast.error(data.discount.error);
      else if (data.discount) toast.success("Discount applied.");
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const place = async () => {
    if (!form.email || !form.address || !form.city || !form.postal) return toast.error("Please complete your delivery details.");
    setPlacing(true);
    try {
      const { data } = await api.post("/checkout/place", {
        items, country: country?.code || "CA", province: form.province, currency,
        shipping_method: ship, discount_code: appliedCode, email: form.email,
        shipping_address: { name: `${form.first} ${form.last}`, address: form.address, city: form.city, province: form.province, postal: form.postal, country: country?.name, phone: form.phone },
        subscribe_newsletter: form.subscribe,
      });
      if (data.paypal?.approve_url) { window.location.href = data.paypal.approve_url; return; }
      clearCart();
      navigate(`/confirmation/${data.order_number}`, { state: { unconfigured: !data.payment_configured } });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); setPlacing(false); }
  };

  const F = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const inp = "w-full h-12 px-4 border bg-transparent outline-none text-sm focus:border-[var(--ev-text)] transition-colors";

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-14 grid lg:grid-cols-[1.4fr_1fr] gap-16">
      <div>
        <h1 className="font-serif text-4xl mb-10">Checkout</h1>
        <section className="mb-10">
          <h2 className="eyebrow mb-5">Contact</h2>
          <input data-testid="checkout-email" {...F("email")} type="email" placeholder="Email address" className={inp} style={{ borderColor: "var(--ev-border)" }} />
        </section>
        <section className="mb-10">
          <h2 className="eyebrow mb-5">Delivery Address</h2>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="checkout-first" {...F("first")} placeholder="First name" className={inp} style={{ borderColor: "var(--ev-border)" }} />
            <input data-testid="checkout-last" {...F("last")} placeholder="Last name" className={inp} style={{ borderColor: "var(--ev-border)" }} />
            <input data-testid="checkout-address" {...F("address")} placeholder="Address" className={`${inp} col-span-2`} style={{ borderColor: "var(--ev-border)" }} />
            <input data-testid="checkout-city" {...F("city")} placeholder="City" className={inp} style={{ borderColor: "var(--ev-border)" }} />
            {country?.code === "CA" ? (
              <select data-testid="checkout-province" {...F("province")} className={inp} style={{ borderColor: "var(--ev-border)" }}>
                <option value="">Province</option>
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            ) : (
              <input data-testid="checkout-province" {...F("province")} placeholder="State / Province" className={inp} style={{ borderColor: "var(--ev-border)" }} />
            )}
            <input data-testid="checkout-postal" {...F("postal")} placeholder="Postal / ZIP code" className={inp} style={{ borderColor: "var(--ev-border)" }} />
            <input data-testid="checkout-phone" {...F("phone")} placeholder="Phone (optional)" className={inp} style={{ borderColor: "var(--ev-border)" }} />
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--ev-muted)" }}>Shipping to {country?.name || "your country"} · prices shown in {currency}</p>
        </section>

        <section className="mb-10">
          <h2 className="eyebrow mb-5">Shipping Method</h2>
          <div className="space-y-2">
            {(quote?.shipping_options || []).map((o) => (
              <button key={o.id} data-testid={`ship-${o.id}`} onClick={() => setShip(o.id)}
                className="w-full flex items-center justify-between p-4 border text-left"
                style={{ borderColor: ship === o.id ? "var(--ev-text)" : "var(--ev-border)" }}>
                <div>
                  <div className="text-sm font-medium">{o.name}</div>
                  <div className="text-xs" style={{ color: "var(--ev-muted)" }}>{o.days} · {o.carrier}{o.estimated ? " · estimated" : ""}</div>
                </div>
                <span className="text-sm">{o.free ? "Complimentary" : money(o.price_usd)}</span>
              </button>
            ))}
          </div>
        </section>
      </div>

      <aside className="lg:sticky lg:top-28 h-fit border p-7" style={{ borderColor: "var(--ev-border)", background: "var(--ev-surface)" }}>
        <h2 className="font-serif text-2xl mb-6">Order Summary</h2>
        <div className="space-y-4 max-h-64 overflow-y-auto no-scrollbar mb-6">
          {cart.map((i) => (
            <div key={i.key} className="flex gap-3">
              <img src={i.image} alt="" className="w-14 h-18 object-cover" />
              <div className="flex-1 text-sm">
                <div className="font-serif">{i.name}</div>
                <div className="text-xs" style={{ color: "var(--ev-muted)" }}>{i.storefront} · Qty {i.qty}</div>
              </div>
              <div className="text-sm">{money(i.price_usd * i.qty)}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6">
          <input data-testid="checkout-discount" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code"
            className="flex-1 h-11 px-3 border bg-transparent outline-none text-sm" style={{ borderColor: "var(--ev-border)" }} />
          <button data-testid="checkout-apply-discount" onClick={applyCode} className="px-5 text-[0.65rem] tracking-[0.2em] uppercase border" style={{ borderColor: "var(--ev-text)" }}>Apply</button>
        </div>

        {quote && (
          <div className="space-y-2.5 text-sm border-t pt-5" style={{ borderColor: "var(--ev-border)" }}>
            <Row label="Subtotal" value={money(quote.subtotal_usd)} money={money} />
            {quote.discount_usd > 0 && <Row label={`Discount (${quote.discount?.code})`} value={`– ${money(quote.discount_usd)}`} money={money} accent />}
            <Row label="Shipping" value={quote.shipping_usd === 0 ? "Complimentary" : money(quote.shipping_usd)} money={money} />
            <Row label={quote.tax.label} value={money(quote.tax.amount_usd)} money={money} />
            {quote.duty.amount_usd > 0 && <Row label={quote.duty.label} value={money(quote.duty.amount_usd)} money={money} />}
            <div className="flex justify-between font-serif text-xl pt-3 border-t" style={{ borderColor: "var(--ev-border)" }}>
              <span>Total</span><span>{money(quote.total_usd)}</span>
            </div>
            {quote.duty.estimated && quote.duty.amount_usd > 0 && (
              <p className="text-xs pt-2" style={{ color: "var(--ev-muted)" }}>Duties & import taxes are estimated and collected upfront where possible.</p>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 text-xs my-5" style={{ color: "var(--ev-muted)" }}>
          <input type="checkbox" checked={form.subscribe} onChange={(e) => setForm({ ...form, subscribe: e.target.checked })} />
          Keep me informed of new Maison Évaren arrivals
        </label>

        <button data-testid="checkout-place-order" disabled={placing} onClick={place} className="btn-luxe w-full">
          {placing ? "Processing…" : "Place Order"}
        </button>
        <p className="text-[0.68rem] text-center mt-3" style={{ color: "var(--ev-muted)" }}>Secured server-side · PayPal checkout</p>
      </aside>
    </div>
  );
}

function Row({ label, value, accent }) {
  return <div className="flex justify-between" style={accent ? { color: "var(--ev-accent)" } : { color: "var(--ev-muted)" }}><span>{label}</span><span style={accent ? {} : { color: "var(--ev-text)" }}>{value}</span></div>;
}
